import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageTitle, Table, useList, StatusPill, CARD, INPUT, BTN, BTN_GHOST } from "@/pages/admin/ui";

const EVENT_STATUSES = ["NEW ENQUIRY", "CONTACTED", "QUOTATION SENT", "NEGOTIATION", "CONFIRMED", "PREPARATION", "IN SERVICE", "COMPLETED", "CANCELLED"];
const BULK_STATUSES = ["NEW", "CONTACTED", "PROPOSAL", "NEGOTIATION", "ACTIVE", "ON HOLD", "COMPLETED", "CANCELLED"];

function Pipeline({ title, subtitle, path, statuses, cols, testid }) {
  const { data, loading, reload } = useList(path, [path]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const openRow = (row) => { setSel(row); setForm({ status: row.status, manager_name: row.manager_name || "", notes: row.notes || "", quote_amount: row.quote_amount || 0 }); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${path}/${sel.id}`, form);
      toast.success("Updated");
      setSel(null); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const counts = statuses.map((st) => ({ st, n: data.filter((d) => d.status === st).length }));

  return (
    <div data-testid={`admin-${testid}`}>
      <PageTitle title={title} subtitle={subtitle} />
      <div className="mb-5 flex flex-wrap gap-2">
        {counts.map((c) => (
          <span key={c.st} className="inline-flex items-center gap-2 rounded-md border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs text-[#64748B]">
            {c.st} <span className="font-bold text-[#0F172A]">{c.n}</span>
          </span>
        ))}
      </div>
      {loading ? <div className="text-sm text-[#64748B]">Loading…</div> : (
        <Table headers={[...cols.map((c) => c.label), "Status", "Manager", "Action"]}>
          {data.map((r) => (
            <tr key={r.id} className="hover:bg-[#F8F9FA]">
              {cols.map((c) => <td key={c.key} className="px-4 py-3 text-[#334155]">{r[c.key] || "—"}</td>)}
              <td className="px-4 py-3"><StatusPill status={r.status} /></td>
              <td className="px-4 py-3 text-[#334155]">{r.manager_name || "—"}</td>
              <td className="px-4 py-3"><button className={BTN_GHOST} onClick={() => openRow(r)} data-testid={`${testid}-manage`}>Manage</button></td>
            </tr>
          ))}
          {data.length === 0 && <tr><td colSpan={cols.length + 3} className="px-4 py-10 text-center text-[#94A3B8]">No records yet.</td></tr>}
        </Table>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="sm:max-w-lg">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{sel.name}</DialogTitle></DialogHeader>
              <div className="space-y-2 rounded-md bg-[#F8F9FA] p-3 text-sm text-[#334155]">
                {cols.map((c) => <div key={c.key}><span className="text-[#94A3B8]">{c.label}:</span> {sel[c.key] || "—"}</div>)}
                <div><span className="text-[#94A3B8]">Phone:</span> {sel.phone} · <span className="text-[#94A3B8]">Email:</span> {sel.email}</div>
              </div>
              <div className="space-y-4">
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Status</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className={INPUT} data-testid={`${testid}-status-select`}><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Assigned Manager</label>
                  <input className={INPUT} value={form.manager_name} onChange={(e) => setForm({ ...form, manager_name: e.target.value })} /></div>
                {testid === "events" && <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Quote Amount (₹)</label>
                  <input type="number" className={INPUT} value={form.quote_amount} onChange={(e) => setForm({ ...form, quote_amount: Number(e.target.value) })} /></div>}
                <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Notes</label>
                  <textarea rows={3} className={INPUT} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="flex justify-end gap-2"><button className={BTN_GHOST} onClick={() => setSel(null)}>Close</button>
                  <button className={BTN} onClick={save} disabled={saving} data-testid={`${testid}-save`}>{saving ? "Saving…" : "Save Changes"}</button></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function EventsPipeline() {
  return <Pipeline title="Event Pipeline" subtitle="Track every catering event from enquiry to completion." path="/admin/events" statuses={EVENT_STATUSES} testid="events"
    cols={[{ key: "event_type", label: "Type" }, { key: "event_date", label: "Date" }, { key: "city", label: "City" }, { key: "guest_count", label: "Guests" }]} />;
}
export function BulkPipeline() {
  return <Pipeline title="Bulk & Food Service Contracts" subtitle="Manage recurring and institutional food service pipelines." path="/admin/bulk-contracts" statuses={BULK_STATUSES} testid="bulk"
    cols={[{ key: "service_type", label: "Service" }, { key: "frequency", label: "Frequency" }, { key: "meals_count", label: "Meals" }, { key: "location", label: "Location" }]} />;
}
